import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { splitOnMatch } from '../utils/docSearch'
import type { DocSearchResult } from '../utils/docSearch'
import { cn } from '@/lib/utils'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {splitOnMatch(text, query).map((part, i) =>
        part.match ? (
          <mark key={i} className="rounded-[3px] bg-brand-tint px-px text-inherit">
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  )
}

interface DocsSearchPanelProps {
  query: string
  results: Array<DocSearchResult>
  activeIndex: number
  onHover: (index: number) => void
  onOpen: (result: DocSearchResult) => void
  onReveal: (result: DocSearchResult) => void
  className?: string
}

/**
 * Search results for the docs index (design 4c). Each hit shows its full
 * path, and body hits a quoted excerpt, so deep matches stay placeable.
 * Enter opens; ⌘/Ctrl+Enter reveals the doc in the outline.
 */
export function DocsSearchPanel({ query, results, activeIndex, onHover, onOpen, onReveal, className }: DocsSearchPanelProps) {
  const { t } = useTranslation('projects')

  return (
    <div
      className={cn('flex flex-col gap-0.5 self-start rounded-[14px] border bg-card p-2 shadow-[0_16px_32px_-12px_rgba(31,29,26,0.18)]', className)}
    >
      <p className="px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground" aria-live="polite">
        {t('docs.search.resultsFor', { count: results.length, query: query.trim() })}
      </p>
      {results.length === 0 ? (
        <p className="px-2 pb-3 pt-1 text-sm text-muted-foreground">{t('docs.search.none')}</p>
      ) : (
        <ul role="listbox" id="docs-search-results" aria-label={t('docs.search.resultsAria')} className="flex flex-col gap-0.5">
          {results.map((result, index) => (
            <li
              key={result.page.id}
              id={`docs-search-${result.page.id}`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => onHover(index)}
              onClick={(e) => (e.metaKey || e.ctrlKey ? onReveal(result) : onOpen(result))}
              className={cn(
                'flex cursor-pointer flex-col gap-0.5 rounded-[9px] px-2.5 py-2',
                index === activeIndex && 'bg-background',
              )}
            >
              <span className="flex items-center gap-2 text-[13.5px] font-medium">
                <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">
                  {result.titleMatch ? <Highlighted text={result.page.title || t('docs.untitled')} query={query} /> : result.page.title || t('docs.untitled')}
                </span>
              </span>
              {(result.path.length > 0 || result.snippet) && (
                <span className="truncate pl-[22px] text-[11.5px] text-muted-foreground">
                  {result.path.join(' / ')}
                  {result.path.length > 0 && result.snippet ? ' · ' : ''}
                  {result.snippet && (
                    <>
                      “{result.snippet.before}
                      <mark className="rounded-[3px] bg-brand-tint px-px text-inherit">{result.snippet.match}</mark>
                      {result.snippet.after}”
                    </>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-1 flex items-center gap-2 border-t px-2.5 pb-1 pt-2.5 text-[11.5px] text-muted-foreground">
        <kbd className="rounded border px-1 font-mono">↵</kbd>
        {t('docs.search.open')}
        <kbd className="ml-1.5 rounded border px-1 font-mono">{getPlatformShortcutKeys('↵').join('')}</kbd>
        {t('docs.search.reveal')}
      </div>
    </div>
  )
}
